import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ChevronRight, FileDown, Phone } from "lucide-react";
import { Container, Reveal, BtnLink, CheckList, SectionHead, TextLink } from "../components/site/Primitives";
import { DownloadDialog } from "../components/site/DownloadDialog";
import { WhatsAppIcon } from "../components/site/WhatsAppIcon";
import { IMG, CONTACT, PRODUCTS, getProduct } from "../data/site";

const Crumbs = ({ name }) => (
  <nav data-testid="product-breadcrumb" className="flex items-center gap-2 text-xs text-slate-500">
    <Link to="/" className="hover:text-navy">Home</Link><ChevronRight className="h-3 w-3" />
    <Link to="/products" className="hover:text-navy">Products</Link><ChevronRight className="h-3 w-3" />
    <span className="text-navy font-medium">{name}</span>
  </nav>
);

const ColourGallery = ({ p }) => {
  const [sel, setSel] = useState(null);
  const current = sel === null ? { img: p.img, label: "Full colour range" } : { img: p.colours[sel].img, label: p.colours[sel].name || p.colours[sel].hex };
  return (
    <div>
      <div className="relative aspect-[3/2] overflow-hidden bg-mist border border-slate-100">
        <img key={current.img} src={IMG(current.img)} alt={`${p.name} — ${current.label}`} data-testid="product-main-image" className="h-full w-full object-contain animate-fade-down" />
        <span data-testid="product-image-label" className="absolute left-4 top-4 bg-white/90 px-3 py-1.5 text-xs font-semibold text-navy">{current.label}</span>
      </div>
      <p className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Tap a colour to preview <span className="ml-2 text-navy">{p.colours.length}</span></p>
      <div className="mt-4 flex flex-wrap gap-2.5">
        <button onClick={() => setSel(null)} data-testid="swatch-all" className={`h-8 px-3 text-xs font-semibold border transition-colors ${sel === null ? "border-navy bg-navy text-white" : "border-slate-300 text-slate-600 hover:border-navy"}`}>All</button>
        {p.colours.map((c, i) => (
          <button key={c.img} onClick={() => setSel(i)} title={c.name || c.hex} aria-label={`Show ${c.name || c.hex}`} data-testid={`swatch-${i}`}
            className={`h-8 w-8 rounded-full ring-offset-2 transition-transform duration-200 hover:scale-110 ${sel === i ? "ring-2 ring-navy" : "ring-1 ring-black/10"}`} style={{ background: c.hex }} />
        ))}
      </div>
    </div>
  );
};

const PhotoGallery = ({ p }) => {
  const [i, setI] = useState(0);
  return (
    <div>
      <div className="aspect-[3/2] overflow-hidden bg-mist border border-slate-100">
        <img key={i} src={IMG(p.photos[i])} alt={p.name} data-testid="product-main-image" className="h-full w-full object-cover animate-fade-down" />
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        {p.photos.map((ph, k) => (
          <button key={ph} onClick={() => setI(k)} data-testid={`product-thumb-${k}`} className={`aspect-[3/2] overflow-hidden bg-mist border-2 transition-colors ${i === k ? "border-navy" : "border-transparent hover:border-slate-300"}`}>
            <img src={IMG(ph)} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
};

const SpecTable = ({ specs }) => (
  <dl data-testid="product-spec-table" className="border border-slate-200">
    {specs.map(([k, v], i) => (
      <div key={k} className={`grid grid-cols-[150px_1fr] sm:grid-cols-[190px_1fr] text-sm ${i ? "border-t border-slate-100" : ""}`}>
        <dt className="bg-mist px-5 py-3.5 font-semibold text-slate-500">{k}</dt>
        <dd className="px-5 py-3.5 text-ink">{v}</dd>
      </div>
    ))}
  </dl>
);

const GrammageTable = ({ rows, bag }) => {
  const necks = [...new Set(rows.map((r) => r[0]))];
  const [neck, setNeck] = useState(necks[0]);
  return (
    <section className="bg-mist py-20 lg:py-24" data-testid="grammage-section">
      <Container>
        <SectionHead eyebrow="Grammage table" title={`${rows.length} grammages, ${necks[0]} neck`} />
        <div className={`mt-10 flex gap-2 ${necks.length < 2 ? "hidden" : ""}`}>
          {necks.map((n) => (
            <button key={n} onClick={() => setNeck(n)} data-testid={`neck-tab-${n.split(" ")[0].replace("/", "-")}`} className={`btn h-11 ${neck === n ? "bg-navy text-white" : "border border-slate-300 bg-white text-slate-600 hover:border-navy"}`}>{n}</button>
          ))}
        </div>
        <div className="mt-6 overflow-x-auto border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="grammage-table">
            <thead className="bg-brand-50 text-left text-navy"><tr>{["Neck finish", "Application", "Grammage", "Bag weight"].map((h) => <th key={h} className="px-5 py-4 text-xs font-semibold uppercase tracking-wider">{h}</th>)}</tr></thead>
            <tbody>
              {rows.filter((r) => r[0] === neck).map(([n, a, g]) => (
                <tr key={g} className="border-t border-slate-100 hover:bg-brand-50/60"><td className="px-5 py-3.5 font-medium">{n}</td><td className="px-5 py-3.5 text-slate-600">{a}</td><td className="px-5 py-3.5 font-display font-semibold text-navy">{g}</td><td className="px-5 py-3.5 text-slate-600">{bag}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </Container>
    </section>
  );
};

const Actions = ({ p }) => (
  <div className="mt-8 flex flex-wrap gap-3">
    <BtnLink to="/contact" testId="product-quote-btn">Request a quote</BtnLink>
    <a href={`${CONTACT.whatsapp}?text=${encodeURIComponent(`Hello MU Plast, I'd like details on ${p.name}.`)}`} target="_blank" rel="noreferrer" data-testid="product-whatsapp-btn" className="btn bg-[#128C7E] text-white hover:brightness-95"><WhatsAppIcon className="h-4 w-4" /> WhatsApp enquiry</a>
    <DownloadDialog resource="brochure">
      <button data-testid="product-brochure-btn" className="btn border border-navy/25 text-navy hover:bg-navy hover:text-white"><FileDown className="h-4 w-4" /> Brochure</button>
    </DownloadDialog>
  </div>
);

const Related = ({ id }) => (
  <section className="py-20 lg:py-24">
    <Container>
      <SectionHead eyebrow="Explore the range" title="Other products" />
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {PRODUCTS.filter((x) => x.id !== id).map((x) => (
          <Link key={x.id} to={`/products/${x.id}`} data-testid={`related-${x.id}`} className="lift group border border-slate-200 bg-white">
            <div className="img-zoom aspect-[3/2] overflow-hidden bg-mist"><img src={IMG(x.img)} alt={x.name} className="h-full w-full object-cover" /></div>
            <div className="flex items-center justify-between p-5"><span className="font-display font-semibold text-ink">{x.name}</span><TextLink to={`/products/${x.id}`}>View</TextLink></div>
          </Link>
        ))}
      </div>
    </Container>
  </section>
);

export default function ProductDetail() {
  const { id } = useParams();
  const p = getProduct(id);
  if (!p) return <Navigate to="/products" replace />;
  const isPreform = !p.colours;
  return (
    <div data-testid={`product-detail-${p.id}`}>
      <section className="pt-10 pb-20 lg:pb-24">
        <Container>
          <Crumbs name={p.name} />
          <div className="mt-8 grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
            <Reveal>{isPreform ? <PhotoGallery p={p} /> : <ColourGallery p={p} />}</Reveal>
            <Reveal delay={0.1}>
              {p.tag && <span className="border border-brand/30 bg-brand-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-brand-600">{p.tag}</span>}
              <h1 data-testid="product-title" className="font-display mt-5 text-4xl sm:text-5xl font-semibold tracking-tight text-ink">{p.name}</h1>
              <p className="mt-5 text-base md:text-lg leading-relaxed text-slate-600">{p.blurb}</p>
              <div className="mt-7"><CheckList items={p.features} /></div>
              <Actions p={p} />
              <div className="mt-6 flex items-center gap-2 text-sm text-slate-500"><Phone className="h-4 w-4" /> Sales: <a href={`tel:${CONTACT.phones[0].tel}`} className="font-semibold text-navy hover:text-brand">{CONTACT.phones[0].label}</a></div>
            </Reveal>
          </div>
          <Reveal className="mt-20">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-ink">Specifications</h2>
            <div className="mt-6"><SpecTable specs={p.specs} /></div>
            <p className="mt-4 text-sm text-slate-500">Technical datasheets, samples, and line trials can be arranged through our sales team.</p>
          </Reveal>
        </Container>
      </section>
      {isPreform && <GrammageTable rows={p.rows} bag={p.bag} />}
      <Related id={p.id} />
    </div>
  );
}
